import { gql, useQuery } from "@apollo/client";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import Button from "../../../components/Button";
import CardField from "../../../components/CardField";
import { FormError } from "../../../components/forms/shared";
import { ROUTE_HOME } from "../../../routes";
import { createPriorityWaitlistCard } from "../../../services/billing";
import { logout } from "../../../store/modules/user/actions";
import { pushEvent } from "../../../utils/analytics";
import { StepContainer } from "../components/shared";

const Summary = () => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const { data, refetch } = useQuery(gql`
    {
      onboarding {
        priorityWaitlist
      }
    }
  `);

  const { priorityWaitlist } =
    data && data.onboarding
      ? data.onboarding
      : {
          priorityWaitlist: false,
        };

  const joinPriorityWaitlist = () => {
    setLoading(true);
    setError("");
    if (elements && stripe) {
      const cardElement = elements.getElement(CardElement);
      if (cardElement) {
        stripe.createToken(cardElement, {}).then(({ token }) => {
          if (token) {
            createPriorityWaitlistCard(token.id).subscribe(
              () => {
                refetch();
                setLoading(false);
                pushEvent({ event: "paid_plan" });
              },
              () => {
                refetch();
                setLoading(false);
                setError("Card error. Please try again later.");
              }
            );
          }
        });
      }
    }
  };

  return (
    <StepContainer className="lg:w-4/12 md:w-6/12 flex flex-col">
      <div className="flex flex-col border border-gray-400 rounded-md bg-white p-8">
        <div className="prose prose-lg text-center w-full">
          <h3>Thank you for joining the wait list</h3>
          <p>
            Here at Due we are working hard to bring you the next revolutionary
            retirement plans. Please bear with us as we are working hard to
            create the best experience for our users. We will reach out when we
            launch.
          </p>
        </div>
        <div className="w-full h-px my-6 bg-gray-200"></div>
        {!priorityWaitlist && (
          <>
            <div className="prose prose-lg text-center w-full">
              <h3> Priority Waitlist</h3>
              <p>
                By joining the priority wait list you will be the one of the
                first people to be able to participate in Due's retirement
                plans. Enter information below to sign up. Nothing will be
                charged to your card at this time. It is only used to hold your
                spot.
              </p>
            </div>
            {error && <FormError className="mt-6">{error}</FormError>}
            <div className="mt-6">
              <CardField disabled={loading} />
              <div className="flex justify-center mt-6">
                <Button
                  onClick={() => {
                    joinPriorityWaitlist();
                  }}
                  loading={loading}
                >
                  Join Priority List
                </Button>
              </div>
            </div>
          </>
        )}

        {priorityWaitlist && (
          <>
            <div className="prose prose-lg text-center w-full">
              <h3>Joined priority wait list</h3>
              <p>
                Thanks for joining the priority wait list. You are now in queue
                to be one of the first people to start with Due.
              </p>
            </div>
          </>
        )}
        <div className="w-full h-px bg-gray-200 mt-6"></div>
        <div className="flex justify-center mt-6">
          <Button
            color="transparent"
            size="lg"
            onClick={() => dispatch(logout())}
          >
            Logout
          </Button>
          <Button
            color="transparent"
            className="ml-2"
            size="lg"
            onClick={() => history.push(ROUTE_HOME)}
          >
            Go To Dashboard
          </Button>
        </div>
      </div>
    </StepContainer>
  );
};

export default Summary;
